import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "@fortawesome/fontawesome-free/css/all.min.css";
import {
  formatDate,
  getDataBlockBySection,
} from "../../../utils/helpers";
import {
  LEARNING_ITEM_TYPES,
  getLearningItemConfig,
  getLearningItemType,
} from "./learningItemConfig";
import {
  extractRichHeroParts,
  getDateRange,
  getEmbeddableVideoUrl,
  getInstructorName,
  getPaymentPlans,
  getPlanDescriptionItems,
  getWeeksBetween,
  hasHtmlContent,
  replaceLearningItemPlaceholders,
} from "./learningItemUtils";
import "./LearningItemDetailsPage.css";

function LearningItemDetailsPage({ data }) {
  const [selectedPlanIndex, setSelectedPlanIndex] = useState(0);
  const [activeTab, setActiveTab] = useState("overview");
  const [isVideoPlayerVisible, setIsVideoPlayerVisible] = useState(false);
  const learningItemType = getLearningItemType(data?.type);
  const learningItemConfig = getLearningItemConfig(learningItemType);
  const isSimpleCourse = learningItemType === LEARNING_ITEM_TYPES.COURSE;

  useEffect(() => {
    setIsVideoPlayerVisible(false);
  }, [data?.coverVideo]);

  useEffect(() => {
    document.body.classList.add("learning-item-details-active");

    return () => {
      document.body.classList.remove("learning-item-details-active");
    };
  }, []);

  const guaranteeContent = useMemo(
    () => getDataBlockBySection(data?.richTextOthers, "guarantee"),
    [data?.richTextOthers]
  );
  const ctaContent = useMemo(
    () => getDataBlockBySection(data?.richTextOthers, "cta"),
    [data?.richTextOthers]
  );
  const weeksBetween = getWeeksBetween(data?.startDate, data?.endDate);
  const paymentPlans = useMemo(
    () => (learningItemConfig.usesPaymentPlans ? getPaymentPlans(data) : []),
    [data, learningItemConfig.usesPaymentPlans]
  );
  const selectedPlan = paymentPlans[selectedPlanIndex] || paymentPlans[0];
  const selectedPlanDescriptionItems = useMemo(
    () =>
      learningItemConfig.usesPaymentPlans
        ? getPlanDescriptionItems(selectedPlan?.description)
        : [],
    [learningItemConfig.usesPaymentPlans, selectedPlan?.description]
  );
  const handlePlanSelect = (index) => {
    setSelectedPlanIndex(index);
  };
  const handlePaymentOptionsClick = (event) => {
    const option = event.target.closest("[data-plan-index]");
    if (!option) return;

    handlePlanSelect(Number(option.dataset.planIndex));
  };
  const coverUrl =
    data?.coverPicture ||
    data?.bannerImage ||
    "/assets/images/preview/course-details.png";
  const heroBackgroundStyle = data?.bannerImage
    ? {
        backgroundImage: `url(${data.bannerImage})`,
      }
    : null;
  const videoPlayerUrl = useMemo(
    () => getEmbeddableVideoUrl(data?.coverVideo),
    [data?.coverVideo]
  );
  const hasVideo = Boolean(videoPlayerUrl);
  const showVideoPlayer = hasVideo && isVideoPlayerVisible;
  const isOpened = data?.status === "OPENED";
  const durationLabel = weeksBetween ? `${weeksBetween} weeks` : "8 weeks";
  const dateRangeLabel = getDateRange(data?.startDate, data?.endDate);
  const moduleCount = Array.isArray(data?.modules) ? data.modules.length : 0;
  const lessonCount = Number(data?.numberLessons) || 0;
  const moduleLabel = `${moduleCount} module${moduleCount === 1 ? "" : "s"}`;
  const lessonLabel = `${lessonCount} lesson${lessonCount === 1 ? "" : "s"}`;
  const instructorName = getInstructorName(data?.author);
  const priceLabel =
    data?.price === null || data?.price === undefined
      ? "To be announced"
      : `$${data.price}`;
  const learningItemPlaceholderValues = useMemo(
    () => ({
      "session.title": data?.title || "",
      "session.duration": durationLabel,
      "session.startDate": data?.startDate ? formatDate(data.startDate) : "To be announced",
      "session.endDate": data?.endDate ? formatDate(data.endDate) : "To be announced",
      "session.dateRange": dateRangeLabel,
      "session.language": "English",
      "session.level": "Beginner to Intermediate",
      "course.title": data?.title || "",
      "course.modules": moduleLabel,
      "course.lessons": lessonLabel,
      "course.instructor": instructorName,
      "course.price": priceLabel,
      "course.language": "English",
      "course.level": "Beginner to Intermediate",
    }),
    [
      data?.title,
      data?.startDate,
      data?.endDate,
      durationLabel,
      dateRangeLabel,
      instructorName,
      lessonLabel,
      moduleLabel,
      priceLabel,
    ]
  );
  const richTextHeroHtml = useMemo(
    () =>
      replaceLearningItemPlaceholders(
        data?.richTextHero,
        learningItemPlaceholderValues
      ),
    [data?.richTextHero, learningItemPlaceholderValues]
  );
  const richHeroParts = useMemo(
    () => extractRichHeroParts(richTextHeroHtml),
    [richTextHeroHtml]
  );
  const richTextCurriculumHtml = data?.richTextCurriculum || "";
  const richTextFAQHtml = data?.richTextFAQ || "";
  const curriculumModules = useMemo(() => {
    if (!isSimpleCourse || !Array.isArray(data?.modules)) return [];

    return [...data.modules]
      .filter((module) => module?.publicationStatus !== false)
      .sort((firstModule, secondModule) => {
        const firstOrder = Number(firstModule?.order) || 0;
        const secondOrder = Number(secondModule?.order) || 0;
        return firstOrder - secondOrder;
      });
  }, [data?.modules, isSimpleCourse]);
  const hasRichTextHero = hasHtmlContent(richHeroParts.mainHtml);
  const hasRichHeroBenefits = hasHtmlContent(richHeroParts.benefitsHtml);
  const hasRichCurriculum = hasHtmlContent(richTextCurriculumHtml);
  const hasCurriculum =
    hasRichCurriculum || curriculumModules.length > 0;
  const hasFAQ = hasHtmlContent(richTextFAQHtml);
  const contentTabs = [
    ["overview", "Overview"],
    ...(hasCurriculum ? [["curriculum", "Curriculum"]] : []),
    ...(hasFAQ ? [["faq", "FAQ"]] : []),
  ];
  const heroEyebrow = learningItemConfig.heroEyebrow;
  const heroSubtitle = learningItemConfig.heroSubtitle;
  const heroMetaItems = isSimpleCourse
    ? [
        {
          icon: "fa-solid fa-book-open",
          value: lessonLabel,
          label: "Lessons",
        },
        {
          icon: "fa-solid fa-layer-group",
          value: moduleLabel,
          label: "Modules",
        },
        {
          icon: "fa-solid fa-globe",
          value: "English",
          label: "Language",
        },
        {
          icon: "fa-solid fa-arrow-trend-up",
          value: "Beginner to Intermediate",
          label: "Level",
          highlighted: true,
        },
      ]
    : [
        {
          icon: "fa-regular fa-clock",
          value: durationLabel,
          label: "Duration",
        },
        {
          icon: "fa-regular fa-calendar",
          value: dateRangeLabel,
          label: "Start - End Date",
        },
        {
          icon: "fa-solid fa-globe",
          value: "English",
          label: "Language",
        },
        {
          icon: "fa-solid fa-arrow-trend-up",
          value: "Beginner to Intermediate",
          label: "Level",
          highlighted: true,
        },
      ];
  const ctaTitle =
    ctaContent?.title || learningItemConfig.defaultCtaTitle;
  const ctaDescription =
    ctaContent?.description || learningItemConfig.defaultCtaDescription;
  const hasGuaranteeContent =
    Boolean(guaranteeContent?.title) || Boolean(guaranteeContent?.description);

  const enrollLink = isSimpleCourse
    ? data?.paymentLink || "#payment-options"
    : selectedPlan?.paymentLink || data?.paymentLink || "#payment-options";
  const detailItems = isSimpleCourse
    ? [
        {
          icon: "fa-solid fa-user-tie",
          label: "Instructor",
          value: instructorName,
        },
        {
          icon: "fa-solid fa-layer-group",
          label: "Modules",
          value: moduleLabel,
        },
        {
          icon: "fa-solid fa-book-open",
          label: "Lessons",
          value: lessonLabel,
        },
        {
          icon: "fa-solid fa-globe",
          label: "Language",
          value: "English",
        },
        {
          icon: "fa-solid fa-arrow-trend-up",
          label: "Level",
          value: "Beginner to intermediate",
        },
        {
          icon: "fa-solid fa-certificate",
          label: "Certificate",
          value: "Yes",
        },
        {
          icon: "fa-solid fa-key",
          label: "Access",
          value: "Lifetime access to materials",
        },
      ]
    : [
        {
          icon: "fa-regular fa-clock",
          label: "Duration",
          value: durationLabel,
        },
        {
          icon: "fa-solid fa-calendar-day",
          label: "Start date",
          value: data?.startDate
            ? formatDate(data.startDate)
            : "To be announced",
        },
        {
          icon: "fa-regular fa-calendar",
          label: "End date",
          value: data?.endDate
            ? formatDate(data.endDate)
            : "To be announced",
        },
        {
          icon: "fa-solid fa-globe",
          label: "Language",
          value: "English",
        },
        {
          icon: "fa-solid fa-arrow-trend-up",
          label: "Level",
          value: "Beginner to intermediate",
        },
        {
          icon: "fa-solid fa-certificate",
          label: "Certificate",
          value: "Yes",
        },
        {
          icon: "fa-solid fa-key",
          label: "Access",
          value: "Lifetime access to materials",
        },
      ];
  const safeActiveTab = contentTabs.some(([tabKey]) => tabKey === activeTab)
    ? activeTab
    : "overview";

  return (
    <div
      className="ns-course-page"
      data-learning-item-type={learningItemType}
    >
      <div className="ns-breadcrumb-band">
        <div className="ns-container ns-breadcrumb">
          <Link to="/">Home</Link>
          <i className="fa-solid fa-chevron-right"></i>
          <span>All Courses</span>
          <i className="fa-solid fa-chevron-right"></i>
          <strong>{data?.title}</strong>
        </div>
      </div>

      <section
        className={`ns-course-hero ${data?.bannerImage ? "has-banner-image" : ""}`}
        style={heroBackgroundStyle}
      >
        <div className="ns-container ns-hero-content">
          {hasRichTextHero ? (
            <div
              className="ns-hero-copy ns-hero-rich"
              dangerouslySetInnerHTML={{ __html: richHeroParts.mainHtml }}
            ></div>
          ) : (
            <div className="ns-hero-copy">
              <span className="ns-eyebrow">{heroEyebrow}</span>
              <h1>{data?.title}</h1>
              <p>{heroSubtitle}</p>

              <div className="ns-hero-meta">
                {heroMetaItems.map((item) => (
                  <div
                    className={`ns-hero-meta-item ${
                      item.highlighted ? "ns-hero-meta-level" : ""
                    }`}
                    key={item.label}
                  >
                    <i className={item.icon}></i>
                    <div>
                      <strong>{item.value}</strong>
                      <span>{item.label}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {hasRichHeroBenefits ? (
          <div dangerouslySetInnerHTML={{ __html: richHeroParts.benefitsHtml }}></div>
        ) : null}
      </section>

      <main className="ns-container ns-main-grid">
        <section className="ns-content-column">
          <nav className="ns-content-tabs">
            {contentTabs.map(([tabKey, label]) => (
              <button
                key={tabKey}
                className={safeActiveTab === tabKey ? "is-active" : ""}
                type="button"
                onClick={() => setActiveTab(tabKey)}
              >
                {label}
              </button>
            ))}
          </nav>

          {safeActiveTab === "overview" ? (
            <>
              <h2 className="ns-section-title">About this course</h2>
              <div
                className="ns-course-description"
                dangerouslySetInnerHTML={{
                  __html:
                    data?.description ||
                    "LLMOps blends DevOps practices with Large Language Models (LLMs) to build, deploy, and scale AI-powered applications reliably. This program gives you the practical skills and tools to manage the full lifecycle of LLM-based systems.",
                }}
              ></div>


            </>
          ) : null}

          {safeActiveTab === "curriculum" && hasCurriculum ? (
            <div className="ns-tab-panel">
              <h2 className="ns-section-title">Curriculum</h2>
              {hasRichCurriculum ? (
                <div
                  className="ns-rich-tab-content"
                  dangerouslySetInnerHTML={{ __html: richTextCurriculumHtml }}
                ></div>
              ) : (
                <div className="ns-module-list">
                  {curriculumModules.map((module, index) => (
                    <article className="ns-module-card" key={module.id}>
                      <div>
                        <span>Module {module.order || index + 1}</span>
                        <h3>{module.title || `Module ${index + 1}`}</h3>
                        {hasHtmlContent(module.description) ? (
                          <div
                            className="ns-module-description"
                            dangerouslySetInnerHTML={{
                              __html: module.description,
                            }}
                          ></div>
                        ) : null}
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </div>
          ) : null}

          {safeActiveTab === "faq" && hasFAQ ? (
            <div className="ns-tab-panel">
              <h2 className="ns-section-title">FAQ</h2>
              <div
                className="ns-rich-tab-content ns-faq-content"
                dangerouslySetInnerHTML={{ __html: richTextFAQHtml }}
              ></div>
            </div>
          ) : null}


          <div className="ns-cta-panel">
            <div>
              <h2>{ctaTitle}</h2>
              <p>{ctaDescription}</p>
            </div>
            <div className="ns-cta-actions">
              <a
                className={`ns-primary-btn ${!isOpened ? "is-disabled" : ""}`}
                href={isOpened ? enrollLink : "#payment-options"}
                target={isOpened ? "_blank" : undefined}
                rel={isOpened ? "noopener noreferrer" : undefined}
                aria-disabled={!isOpened}
              >
                {isOpened
                  ? learningItemConfig.openActionLabel
                  : learningItemConfig.closedActionLabel}
                <i className="fa-solid fa-arrow-right"></i>
              </a>
            </div>
          </div>
        </section>

        <aside className="ns-sidebar-column">
          <div className="ns-sticky-sidebar">
            <div className="ns-purchase-card" id="payment-options">
              <div
                className={`ns-preview-card ${showVideoPlayer ? "has-video" : ""}`}
                style={
                  !showVideoPlayer && coverUrl
                    ? { backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.18), rgba(15, 23, 42, 0.42)), url(${coverUrl})` }
                    : null
                }
                role={!hasVideo ? "img" : undefined}
                aria-label={!hasVideo ? `${data?.title || "Course"} cover` : undefined}
              >
                {showVideoPlayer ? (
                  <iframe
                    title={`${data?.title || "Course"} preview`}
                    src={videoPlayerUrl}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                ) : hasVideo ? (
                  <button
                    className="ns-preview-overlay"
                    type="button"
                    onClick={() => setIsVideoPlayerVisible(true)}
                    aria-label={`Play ${data?.title || "course"} preview video`}
                  >
                    <span>
                      <i className="fa-solid fa-play"></i>
                    </span>
                    <strong>{learningItemConfig.previewLabel}</strong>
                  </button>
                ) : null}
              </div>

              <div className="ns-purchase-body">
                <h3>{learningItemConfig.purchaseTitle}</h3>
                {learningItemConfig.usesPaymentPlans ? (
                  <div
                    className="ns-payment-options"
                    role="radiogroup"
                    onClick={handlePaymentOptionsClick}
                  >
                    {paymentPlans.map((plan, index) => (
                      <button
                        className={`ns-payment-option ${
                          selectedPlanIndex === index ? "is-selected" : ""
                        }`}
                        data-plan-index={index}
                        key={plan.id}
                        type="button"
                        role="radio"
                        aria-checked={selectedPlanIndex === index}
                        onPointerDown={() => handlePlanSelect(index)}
                      >
                        <span className="ns-radio" aria-hidden="true"></span>
                        <span>
                          <strong>
                            {plan.label}
                            {plan.price ? `: $${plan.price}` : ""}
                          </strong>
                          {plan.sessionName ? (
                            <small>{plan.sessionName}</small>
                          ) : null}
                        </span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="ns-course-price-summary">
                    <span>One-time payment</span>
                    <strong>{priceLabel}</strong>
                  </div>
                )}

                <a
                  className={`ns-enroll-btn ${!isOpened ? "is-disabled" : ""}`}
                  href={isOpened ? enrollLink : "#payment-options"}
                  target={isOpened ? "_blank" : undefined}
                  rel={isOpened ? "noopener noreferrer" : undefined}
                  aria-disabled={!isOpened}
                >
                  {isOpened
                    ? learningItemConfig.openActionLabel
                    : learningItemConfig.closedActionLabel}
                  <i className="fa-solid fa-arrow-right"></i>
                </a>

                {learningItemConfig.usesPaymentPlans &&
                selectedPlanDescriptionItems.length ? (
                  <ul className="ns-includes-list">
                    {selectedPlanDescriptionItems.map((item, index) => (
                      <li key={`${selectedPlan?.id || "plan-description"}-${index}`}>
                        <i className="fa-solid fa-check"></i>
                        {item}
                      </li>
                    ))}
                  </ul>
                ) : null}

              </div>
            </div>

            <div className="ns-detail-list">
              {detailItems.map((item) => (
                <DetailRow
                  icon={item.icon}
                  key={item.label}
                  label={item.label}
                >
                  {item.value}
                </DetailRow>
              ))}
            </div>

            {hasGuaranteeContent ? (
              <div className="ns-guarantee-card">
                <span>
                  <i className="fa-regular fa-circle-check"></i>
                </span>
                <div>
                  {guaranteeContent?.title ? (
                    <strong>{guaranteeContent.title}</strong>
                  ) : null}
                  {guaranteeContent?.description ? (
                    <p>{guaranteeContent.description}</p>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
        </aside>
      </main>
    </div>
  );
}

function DetailRow({ icon, label, children }) {
  return (
    <div className="ns-detail-row">
      <span className="ns-detail-icon">
        <i className={icon}></i>
      </span>
      <strong>{label}</strong>
      <span>{children}</span>
    </div>
  );
}

export default LearningItemDetailsPage;
